/**
 * Backoffice de statistiques (route `/stats`) — accès opérateur derrière login.
 *
 * Login email/mdp → cookie de session (Rust) → GET /api/stats (agrégats
 * serveur). Affiche : nombre de parties, questions répondues, et les profils
 * politiques qui se dégagent (archétypes / courants / axes moyens).
 */

import { lazy, Suspense, useEffect, useState, type FormEvent } from "react";
import { ARCHETYPE_NAME } from "../engine/archetypes";
import { COURANT_NAME } from "../engine/courant";
import "../styles/stats.css";

interface Dist {
  key: string;
  count: number;
}

interface Stats {
  games_started: number;
  games_ended: number;
  questions_answered: number;
  country_archetypes: Dist[];
  sources: Dist[];
  political_archetypes: Dist[];
  courants: Dist[];
  end_causes: Dist[];
  avg_axes: {
    economic: number | null;
    societal: number | null;
    international: number | null;
    authority: number | null;
  };
  feedback: {
    responses: number;
    interest: number | null;
    themes: number | null;
    mechanics: number | null;
    objectives: number | null;
    satisfaction: number | null;
    replay: number | null;
  };
  donations?: { count: number; total_eur: number };
}

const ARCH_NAMES = ARCHETYPE_NAME as Record<string, string>;
const COURANT_NAMES = COURANT_NAME as Record<string, string>;

// Sous-pages listées, chargées en lazy (code-split) au clic sur un encart.
const GamesList = lazy(() => import("./StatsGamesList"));
const QuestionsList = lazy(() => import("./StatsQuestionsList"));

type GamesView = "started" | "ended" | "questions";

function viewFromPath(): GamesView | null {
  const p = window.location.pathname;
  if (p === "/stats/started") return "started";
  if (p === "/stats/ended") return "ended";
  if (p === "/stats/questions") return "questions";
  return null;
}

export function StatsDashboard() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [view, setView] = useState<GamesView | null>(viewFromPath);

  const navigate = (v: GamesView | null) => {
    history.pushState({}, "", v ? `/stats/${v}` : "/stats");
    setView(v);
  };

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "same-origin" })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  // Bouton « précédent » du navigateur → resynchronise la sous-page.
  useEffect(() => {
    const onPop = () => setView(viewFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetch("/api/stats", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("stats"))))
      .then((s: Stats) => setStats(s))
      .catch(() => setError("Impossible de charger les statistiques."));
  }, [authed]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email, password }),
      });
      if (r.ok) {
        setAuthed(true);
      } else {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setError(j.error ?? "Connexion refusée.");
      }
    } catch {
      setError("Erreur réseau.");
    }
  };

  const logout = async () => {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
    }).catch(() => {});
    setAuthed(false);
    setStats(null);
  };

  if (authed === null) {
    return <div className="stats-loading">Chargement…</div>;
  }

  if (!authed) {
    return (
      <div className="stats-backdrop">
        <form className="stats-login" onSubmit={submit}>
          <div className="stats-kicker">BACKOFFICE</div>
          <h1>Statistiques</h1>
          <p className="stats-sub">Accès réservé à l'opérateur.</p>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
          <input
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {error && <p className="stats-error">{error}</p>}
          <button type="submit">Se connecter</button>
        </form>
      </div>
    );
  }

  if (view) {
    return (
      <Suspense fallback={<div className="stats-loading">Chargement…</div>}>
        {view === "questions" ? (
          <QuestionsList onBack={() => navigate(null)} />
        ) : (
          <GamesList status={view} onBack={() => navigate(null)} />
        )}
      </Suspense>
    );
  }

  return (
    <div className="stats-page">
      <header className="stats-header">
        <h1>Statistiques · Moi Président</h1>
        <button className="stats-logout" onClick={logout}>
          Déconnexion
        </button>
      </header>

      {!stats ? (
        <p className="stats-muted">{error ?? "Chargement…"}</p>
      ) : (
        <>
          <section className="stats-kpis">
            <Kpi
              label="Parties lancées"
              value={stats.games_started}
              onClick={() => navigate("started")}
            />
            <Kpi
              label="Parties terminées"
              value={stats.games_ended}
              onClick={() => navigate("ended")}
            />
            <Kpi
              label="Questions répondues"
              value={stats.questions_answered}
              onClick={() => navigate("questions")}
            />
            {stats.donations && (
              <Kpi
                label={`Dons (${stats.donations.count})`}
                value={Math.round(stats.donations.total_eur)}
              />
            )}
          </section>

          <DistTable
            title="Profils politiques qui se dégagent"
            rows={stats.political_archetypes}
            names={ARCH_NAMES}
          />
          <DistTable
            title="Courants"
            rows={stats.courants}
            names={COURANT_NAMES}
          />
          <DistTable
            title="États du pays choisis"
            rows={stats.country_archetypes}
          />
          <DistTable title="Sources de trafic" rows={stats.sources} />
          <DistTable title="Fins de mandat" rows={stats.end_causes} />

          <section className="stats-block">
            <h2>Profil moyen (4 axes, −1 → +1)</h2>
            <AxisBar label="Économique" v={stats.avg_axes.economic} />
            <AxisBar label="Sociétal" v={stats.avg_axes.societal} />
            <AxisBar label="International" v={stats.avg_axes.international} />
            <AxisBar label="Autorité" v={stats.avg_axes.authority} />
          </section>

          <section className="stats-block">
            <h2>
              Ressenti des joueurs (1 → 5) · {stats.feedback.responses} réponse
              {stats.feedback.responses > 1 ? "s" : ""}
            </h2>
            {stats.feedback.responses === 0 ? (
              <p className="stats-muted">Aucun retour pour l'instant.</p>
            ) : (
              <>
                <ScoreBar label="Intérêt" v={stats.feedback.interest} />
                <ScoreBar
                  label="Pertinence des thèmes"
                  v={stats.feedback.themes}
                />
                <ScoreBar
                  label="Compréhension des mécaniques"
                  v={stats.feedback.mechanics}
                />
                <ScoreBar
                  label="Clarté du but / objectifs"
                  v={stats.feedback.objectives}
                />
                <ScoreBar
                  label="Plaisir de jeu"
                  v={stats.feedback.satisfaction}
                />
                <ScoreBar label="Envie de rejouer" v={stats.feedback.replay} />
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  onClick,
}: {
  label: string;
  value: number;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div className="stats-kpi-value">{value.toLocaleString("fr-FR")}</div>
      <div className="stats-kpi-label">{label}</div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        className="stats-kpi stats-kpi-link"
        onClick={onClick}
      >
        {inner}
      </button>
    );
  }
  return <div className="stats-kpi">{inner}</div>;
}

function DistTable({
  title,
  rows,
  names,
}: {
  title: string;
  rows: Dist[];
  names?: Record<string, string>;
}) {
  const total = rows.reduce((a, r) => a + r.count, 0);
  return (
    <section className="stats-block">
      <h2>{title}</h2>
      {rows.length === 0 ? (
        <p className="stats-muted">Aucune donnée pour l'instant.</p>
      ) : (
        <ul className="stats-dist">
          {rows.map((r) => {
            const pct = total > 0 ? (r.count / total) * 100 : 0;
            return (
              <li key={r.key} className="stats-dist-row">
                <span className="stats-dist-label">
                  {names?.[r.key] ?? r.key}
                </span>
                <span className="stats-dist-bar" aria-hidden="true">
                  <span
                    className="stats-dist-fill"
                    style={{ width: `${pct.toFixed(1)}%` }}
                  />
                </span>
                <span className="stats-dist-count">
                  {r.count} · {pct.toFixed(0)}%
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ScoreBar({ label, v }: { label: string; v: number | null }) {
  const value = v ?? 0;
  const pct = (value / 5) * 100; // 1..5 → 0..100
  return (
    <div className="stats-axis">
      <span className="stats-axis-label">{label}</span>
      <span className="stats-axis-track" aria-hidden="true">
        <span
          className="stats-axis-fill"
          style={{ left: "0%", width: `${pct.toFixed(1)}%` }}
          data-direction="right"
        />
      </span>
      <span className="stats-axis-value">
        {v === null ? "—" : `${value.toFixed(2)} / 5`}
      </span>
    </div>
  );
}

function AxisBar({ label, v }: { label: string; v: number | null }) {
  const value = v ?? 0;
  const pct = ((value + 1) / 2) * 100; // −1..1 → 0..100
  return (
    <div className="stats-axis">
      <span className="stats-axis-label">{label}</span>
      <span className="stats-axis-track" aria-hidden="true">
        <span className="stats-axis-zero" />
        <span
          className="stats-axis-fill"
          style={{
            left: `${Math.min(50, pct)}%`,
            width: `${Math.abs(pct - 50)}%`,
          }}
          data-direction={value >= 0 ? "right" : "left"}
        />
      </span>
      <span className="stats-axis-value">
        {v === null ? "—" : value.toFixed(2)}
      </span>
    </div>
  );
}
